import React, {ChangeEvent, useMemo, useState, useRef, useEffect, useCallback} from 'react';
import styles from './textInput.module.scss';
import EyeCrossedIcon from '../../assets/icons/eye_crossed.svg?react';
import EyeIcon from '../../assets/icons/eye.svg?react';
import { ValidInputRegTest } from '../../constants/index';
export type TextInputValue = string | number;
export type TextInputChange = (value: TextInputValue) => void;

type Props = {
  label: string | (() => React.ReactNode);
  id: string;
  value: TextInputValue;
  type?: 'text' | 'password' | 'date' | 'number';
  placeholder?: string;
  className?: string;
  onChange: TextInputChange;
  icon?: () => React.ReactNode;
  inline?: boolean;
  hasError?: boolean;
  inDropDown?: boolean;
  subTitle?: string | React.ReactNode;
  min?: string;
  textArea?: boolean;
  readonly?: boolean;
  autoFocus?: boolean;
  small?: boolean;
  allowSpecialCharacters?: boolean;
  showValidationMessage?: boolean;
};

export const MultiLineTextInput = (props: Props) => {
  const { label, id, placeholder, value, onChange, type = 'text', className, icon, inline, hasError, subTitle, allowSpecialCharacters, showValidationMessage } = props;
  const [showPassword, setShowPassword] = useState(false);
  const textareaRef = useRef<any>(null);
  const [showValidationError, setShowValidationError] = useState(false);

  useEffect(() => {
    if (textareaRef && textareaRef.current) {
      // textareaRef.current.style.height = "0px";
      const scrollHeight = textareaRef.current.scrollHeight;
      if (scrollHeight > 0) {
        textareaRef.current.style.height = scrollHeight + "px";
      }
    }
  },[value])

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = `${e.target.value}`;
    
    if (allowSpecialCharacters || ValidInputRegTest.test(inputValue)) {
      // If the input is valid, update the value normally
      onChange(inputValue);
      if (showValidationError) {
        setShowValidationError(false);
      }
    } else {
      // If the input contains invalid characters, strip them out
      const strippedValue = inputValue.split('').filter(char => ValidInputRegTest.test(char)).join('');
      
      // Update the value with the stripped version
      onChange(strippedValue);
      
      // Show validation message if enabled
      if (showValidationMessage) {
        setShowValidationError(true);
      }
    }
  }, [allowSpecialCharacters, onChange, showValidationError, showValidationMessage]);

  const handleShowPassword = useCallback(() => {
    setShowPassword(true);
  }, []);

  const handleHidePassword = useCallback(() => {
    setShowPassword(false);
  }, []);

  const Icon = useMemo(() => {
    let IconComp: React.FunctionComponent | undefined = undefined;

    if (!icon && type === 'password') {
      if (showPassword) {
        IconComp = () => <div className={styles.eyeIcon} onClick={handleHidePassword}><EyeIcon/></div>;
      } else {
        IconComp = () => <div className={styles.eyeIcon} onClick={handleShowPassword}><EyeCrossedIcon/></div>;
      }
    }
    return IconComp;
  }, [handleHidePassword, handleShowPassword, icon, showPassword, type]);

  const classes = useMemo(() => {
    const classes = [styles.wrapper, className];
    if (inline) {
      classes.push(styles.inline);
    }
    if (Icon) {
      classes.push(styles.hasIcon);
    }
    if (value) {
      classes.push(styles.hasValue);
    }
    if (hasError || showValidationError) {
      classes.push(styles.error);
    }
    return classes.join(' ').trim();
  }, [className, inline, Icon, value, hasError, showValidationError]);

  return (
    <div className={classes}>
      {typeof label === 'function' ? label() : <label className={styles.label} htmlFor={id}>{label}</label>}
      <div className={styles.content}>
        <textarea ref={textareaRef} value={value} onChange={handleChange} placeholder={placeholder} className={`${styles.input} ${styles.multiArea}`} id={id} />
        {typeof Icon === 'function' && <div className={styles.iconWrapper}><Icon/></div>}
        {showValidationError ? (
          <div className={styles.subtitle}>
            <p>For security, we automatically strip characters that are not permitted. Please use only the following characters:</p>
            <ul className={styles.errorPoints}>
              <li>A → Z</li>
              <li>a → z</li>
              <li>0, 1 → 9</li>
              <li>; : " ' ` . + = , / ? & @ !</li>
            </ul>
            <p>If you’re having troubles or would like us to add a character, please reach out to Open Parachute support.</p>
          </div>
          ) : (
          subTitle ? <div className={styles.subtitle}>{subTitle}</div> : null 
        )}
      </div>
    </div>
  );
};
